// Wizard Content - All sections in one page
import { t, getLanguage } from '../../i18n.js';
import { ActivityService } from '../../services/domain/activity-service.js';
import { ContactCard } from './components/contact-card.js';
import { EmptyState } from './components/empty-state.js';
import { AddContactFromSignaturePanel } from './components/add-contact-from-signature-panel.js';
import { FileUtils } from './utils/file-utils.js';
import { SearchableDropdown } from './ui-elements/searchable-dropdown.js';
import { SEARCH_DEBOUNCE_MS, MAX_SEARCH_RESULTS } from './wizard-constants.js';

export class WizardContent {
  constructor(wizard) {
    this.wizard = wizard;
    this.allLeads = [];
    this.allCampaigns = [];
    this.leadsDropdownOpen = false;
    this.campaignsDropdownOpen = false;
    this.showExtraContactSearch = false;
  }

  canProceed() {
    // Allow importing even without contacts selected
    return true;
  }

  async render(container) {
    // Load leads and campaigns if not already loaded
    if (this.wizard.leadsData.size === 0) {
      await this.loadLeadsAndCampaigns();
    }

    if (!this.wizard.actionTypesLoaded) {
      await this.loadActionTypes();
    }

    // Create main container with sections
    const mainContainer = document.createElement('div');
    mainContainer.className = 'consolidated-wizard-container';

    // Contacts Section
    const contactsSection = this.createContactsSection();
    mainContainer.appendChild(contactsSection);

    // Leads & Campaigns Section (always visible now)
    const leadsSection = this.createLeadsCampaignsSection();
    mainContainer.appendChild(leadsSection);

    const activityTypeSection = this.createActivityTypeSection();
    if (activityTypeSection) {
      mainContainer.appendChild(activityTypeSection);
    }

    // Notify (sharedWith) Section
    const notifySection = await this.createNotifySection();
    mainContainer.appendChild(notifySection);

    // Attachments Section
    const attachmentsSection = await this.createAttachmentsSection();
    mainContainer.appendChild(attachmentsSection);

    container.appendChild(mainContainer);
  }

  createContactsSection() {
    const section = document.createElement('div');
    section.className = 'wizard-section contacts-section';

    const header = document.createElement('div');
    header.className = 'section-header';
    const desc = this.wizard.item.itemType === 'message' ? t('contacts_desc_email') : t('contacts_desc_appointment');
    header.innerHTML = `
      <h3>${t('contacts_title')}</h3>
      <p>${desc}</p>
    `;
    section.appendChild(header);

    if (this.wizard.item.itemType === 'message') {
      const signaturePanel = new AddContactFromSignaturePanel(this.wizard);
      section.appendChild(signaturePanel.render());
    }

    const contactsList = document.createElement('div');
    contactsList.className = 'contacts-list-wizard';
    this.fillContactsList(contactsList);

    section.appendChild(contactsList);

    // Selection summary
    const summaryContainer = document.createElement('div');
    summaryContainer.className = 'contacts-summary-container';
    this.updateContactsSummary(summaryContainer);
    section.appendChild(summaryContainer);

    return section;
  }

  updateContactsSummary(container) {
    container.innerHTML = '';
    if (this.wizard.selectedContacts.length > 0) {
      const summary = document.createElement('div');
      summary.className = 'selection-summary';
      summary.innerHTML = `
        <span class="summary-icon">✓</span>
        <span>${this.wizard.selectedContacts.length} ${
        this.wizard.selectedContacts.length === 1 ? t('contacts_selected_one') : t('contacts_selected_many')
      }</span>
      `;
      container.appendChild(summary);
    }
  }

  // Method to refresh the contacts summary (called from contact cards)
  refreshContactsSummary() {
    const summaryContainer = document.querySelector('.contacts-summary-container');
    if (summaryContainer) {
      this.updateContactsSummary(summaryContainer);
    }
    
    // Reload leads and campaigns data if contacts are selected
    if (this.wizard.selectedContacts.length > 0) {
      this.reloadLeadsAndCampaigns();
    }
  }

  createContactSearchInput() {
    const wrapper = document.createElement('div');
    wrapper.className = 'contact-search-container';

    const label = document.createElement('label');
    label.className = 'contact-search-label';
    label.textContent = t('contact_search_label');
    wrapper.appendChild(label);

    const dropdown = new SearchableDropdown({
      placeholder: t('contact_search_placeholder'),
      onSearch: async (query, signal) => {
        const { searchContacts } = await import('../../services/api.js');
        console.log('Searching contacts with:', query);
        const result = await searchContacts(query, MAX_SEARCH_RESULTS, signal);
        const contacts = result?.contacts || [];
        console.log('Search results:', contacts.length, 'contacts found');
        return contacts;
      },
      onSelect: (contact) => this.selectContactFromSearch(contact),
      renderItem: (contact, isSelected) => {
        const contactInfo = document.createElement('div');
        contactInfo.className = 'contact-info';
        
        // Contact Name
        const contactName = document.createElement('div');
        contactName.className = 'contact-name';
        contactName.textContent = contact.value || 
          `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || t('unknown_contact');
        contactInfo.appendChild(contactName);
        
        // Company Name (if available)
        if (contact.customers && contact.customers.length > 0 && contact.customers[0].value) {
          const companyName = document.createElement('div');
          companyName.className = 'contact-company';
          companyName.textContent = contact.customers[0].value;
          contactInfo.appendChild(companyName);
        }
        
        // Email (if available)
        if (contact.emails && contact.emails.length > 0) {
          const contactEmail = document.createElement('div');
          contactEmail.className = 'contact-details';
          contactEmail.textContent = contact.emails[0];
          contactInfo.appendChild(contactEmail);
        }
        
        return contactInfo;
      },
      isSelected: (contact) => this.wizard.selectedContacts.some(c => c._id === contact._id),
      multiSelect: true,
      closeOnSelect: false,
      debounceMs: SEARCH_DEBOUNCE_MS,
      showToggleIcon: false, // Contact search doesn't need toggle icon, just search icon
      additionalClasses: {
        input: 'contact-search-input',
        dropdown: 'contact-search-dropdown',
        icon: 'search-icon'
      }
    });

    wrapper.appendChild(dropdown.render());
    return wrapper;
  }

  appendContactSearch(contactsList, restoreSearchValue = '') {
    const searchContainer = this.createContactSearchInput();
    contactsList.appendChild(searchContainer);
    if (restoreSearchValue) {
      const input = searchContainer.querySelector('.contact-search-input');
      if (input) input.value = restoreSearchValue;
    }
    return searchContainer;
  }

  createSearchAnotherContactLink() {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'search-another-contact';
    button.textContent = t('search_another_contact');
    button.addEventListener('click', () => {
      this.showExtraContactSearch = true;
      const list = button.parentNode;
      button.remove();
      const searchContainer = this.appendContactSearch(list);
      const input = searchContainer.querySelector('.contact-search-input');
      if (input) input.focus();
    });
    return button;
  }

  selectContactFromSearch(contact) {
    // Check if already selected
    const isAlreadySelected = this.wizard.selectedContacts.some(c => c._id === contact._id);
    
    if (isAlreadySelected) {
      // Deselect
      this.wizard.selectedContacts = this.wizard.selectedContacts.filter(c => c._id !== contact._id);
    } else {
      // Add to selected contacts
      this.wizard.selectedContacts.push(contact);
    }
    
    console.log('Contact selection updated:', this.wizard.selectedContacts.length, 'contacts selected');
    
    // Update the UI - the search box will stay visible and update checkmarks automatically
    this.refreshContactsSection();
    
    // Update summary
    this.refreshContactsSummary();
  }

  /**
   * Detected contacts stay on screen whether or not they are checked.
   * Search picks that are not in the detected list are shown in addition.
   */
  fillContactsList(contactsList, { restoreSearchValue = '' } = {}) {
    const itemKind = this.wizard.item.itemType === 'message' ? 'email' : 'appointment';
    const detected = this.wizard.contacts;
    const extras = this.wizard.selectedContacts.filter(
      (selected) => !detected.some((contact) => contact._id === selected._id)
    );

    if (detected.length === 0) {
      if (this.wizard.selectedContacts.length === 0) {
        contactsList.appendChild(EmptyState.noContacts(itemKind).render());
      }

      this.appendContactSearch(contactsList, restoreSearchValue);

      if (extras.length > 0) {
        const divider = document.createElement('div');
        divider.className = 'selected-contacts-divider';
        divider.innerHTML = `<h4>${t('selected_contacts_header')}</h4>`;
        contactsList.appendChild(divider);
        extras.forEach((contact) => {
          contactsList.appendChild(new ContactCard(this.wizard, contact).render());
        });
      }
      return;
    }

    detected.forEach((contact) => {
      contactsList.appendChild(new ContactCard(this.wizard, contact).render());
    });
    extras.forEach((contact) => {
      contactsList.appendChild(new ContactCard(this.wizard, contact).render());
    });

    if (this.showExtraContactSearch) {
      this.appendContactSearch(contactsList, restoreSearchValue);
    } else {
      contactsList.appendChild(this.createSearchAnotherContactLink());
    }
  }

  refreshContactsSection() {
    const contactsSection = document.querySelector('.contacts-section');
    if (!contactsSection) return;

    const contactsList = contactsSection.querySelector('.contacts-list-wizard');
    if (!contactsList) return;

    const existingSearchInput = contactsList.querySelector('.contact-search-input');
    const currentSearchValue = existingSearchInput ? existingSearchInput.value : '';

    contactsList.innerHTML = '';
    this.fillContactsList(contactsList, { restoreSearchValue: currentSearchValue });
  }

  async loadActionTypes() {
    const types = await ActivityService.fetchActionTypes();
    this.wizard.actionTypes = types;
  }

  actionTypeLabel(option) {
    const lang = getLanguage();
    return option[lang] || option.en || option.value;
  }

  createActivityTypeSection() {
    const types = this.wizard.actionTypes;
    if (!types.length) return null;

    const section = document.createElement('div');
    section.className = 'wizard-section activity-type-section';

    const header = document.createElement('div');
    header.className = 'section-header';
    header.innerHTML = `
      <h3>${t('activity_type_title')}</h3>
      <p>${t('activity_type_desc')}</p>
    `;
    section.appendChild(header);

    const container = document.createElement('div');
    container.className = 'dropdown-input-container activity-type-container';

    const label = document.createElement('label');
    label.className = 'dropdown-label';
    label.textContent = t('activity_type_label');
    container.appendChild(label);

    const items = types.map((option) => ({
      value: this.actionTypeLabel(option),
      actionValue: option.value
    }));

    const dropdown = new SearchableDropdown({
      placeholder: t('activity_type_placeholder'),
      items,
      onSelect: (item) => this.selectActionType(item),
      onNoneSelected: () => this.selectActionType(null),
      renderItem: (item) => item.value,
      isSelected: (item) => item.actionValue === this.wizard.selectedActionType,
      showNoneOption: true,
      showIcon: false,
      showToggleIcon: true,
      closeOnSelect: true,
      multiSelect: false,
      debounceMs: 0
    });

    container.appendChild(dropdown.render());
    if (this.wizard.selectedActionType) {
      const selected = items.find((item) => item.actionValue === this.wizard.selectedActionType);
      if (selected) dropdown.setValue(selected.value);
    }
    this.activityTypeDropdown = dropdown;
    section.appendChild(container);
    return section;
  }

  selectActionType(item) {
    this.wizard.selectedActionType = item ? item.actionValue : null;
    if (this.activityTypeDropdown) {
      this.activityTypeDropdown.setValue(item ? item.value : '');
    }
  }

  createLeadsCampaignsSection() {
    const section = document.createElement('div');
    section.className = 'wizard-section leads-campaigns-section';

    const header = document.createElement('div');
    header.className = 'section-header';
    header.innerHTML = `
      <h3>${t('leads_campaigns_title')}</h3>
      <p>${t('leads_campaigns_desc')}</p>
    `;
    section.appendChild(header);

    const inputsContainer = document.createElement('div');
    inputsContainer.className = 'leads-campaigns-inputs';

    // Leads dropdown input
    const leadsContainer = this.createDropdownInput('leads', t('select_lead'), this.wizard.leadsData);
    inputsContainer.appendChild(leadsContainer);

    // Campaigns dropdown input
    const campaignsContainer = this.createDropdownInput('campaigns', t('select_campaign'), this.wizard.campaignsData);
    inputsContainer.appendChild(campaignsContainer);

    section.appendChild(inputsContainer);

    return section;
  }

  createDropdownInput(type, placeholder, dataMap) {
    const container = document.createElement('div');
    container.className = `dropdown-input-container ${type}-container`;

    const label = document.createElement('label');
    label.className = 'dropdown-label';
    label.textContent = type === 'leads' ? t('lead_label') : t('campaign_label');
    container.appendChild(label);

    const allItems = Array.from(dataMap.values());
    
    const dropdown = new SearchableDropdown({
      placeholder: placeholder,
      items: allItems,
      onSelect: (item) => this.selectDropdownItem(type, item),
      onNoneSelected: () => this.selectDropdownItem(type, null),
      renderItem: (item, isSelected) => {
        const displayText = type === 'leads' ? 
          (item.value || t('unnamed_lead')) : 
          (item.value || t('unnamed_campaign'));
        
        // Create item with link if ID exists
        if (item._id) {
          const itemContent = document.createElement('div');
          itemContent.className = 'dropdown-item-content';
          
          const textSpan = document.createElement('span');
          textSpan.className = 'dropdown-item-text';
          textSpan.textContent = displayText;
          
          const linkSpan = document.createElement('span');
          linkSpan.className = 'dropdown-item-link';
          linkSpan.innerHTML = '🔗';
          linkSpan.title = type === 'leads' ? t('click_to_open_lead') : t('click_to_open_campaign');
          linkSpan.onclick = (e) => {
            e.stopPropagation();
            this.openItemInNewTab(type, item._id);
          };
          
          itemContent.appendChild(textSpan);
          itemContent.appendChild(linkSpan);
          return itemContent;
        }
        
        return displayText;
      },
      showNoneOption: true,
      showIcon: false,
      showToggleIcon: true, // Show dropdown toggle icon (▼/▲)
      closeOnSelect: true,
      multiSelect: false,
      debounceMs: 0 // No debounce for static dropdowns
    });

    container.appendChild(dropdown.render());
    
    // Store reference for later updates
    if (type === 'leads') {
      this.leadsDropdown = dropdown;
    } else {
      this.campaignsDropdown = dropdown;
    }

    return container;
  }

  selectDropdownItem(type, item) {
    if (type === 'leads') {
      this.wizard.selectedLead = item ? item._id : null;
      if (this.leadsDropdown) {
        this.leadsDropdown.setValue(item ? (item.value || t('unnamed_lead')) : '');
      }
    } else {
      this.wizard.selectedCampaign = item ? item._id : null;
      if (this.campaignsDropdown) {
        this.campaignsDropdown.setValue(item ? (item.value || t('unnamed_campaign')) : '');
      }
    }
    
    // Update navigation
    this.wizard.updateNavigation();
  }

  // Open lead or campaign in new tab
  async openItemInNewTab(type, itemId) {
    try {
      // Get the instance from auth service
      const { getInstance } = await this.wizard.getAuthInstance();
      const instance = getInstance();
      
      if (!instance) {
        console.error('No instance found for opening item');
        return;
      }

      // Construct the URL based on type
      let itemUrl;
      if (type === 'leads') {
        itemUrl = `https://${instance}.assistgo.de/lead/${itemId}/details`;
      } else if (type === 'campaigns') {
        itemUrl = `https://${instance}.assistgo.de/campaign/${itemId}/details`;
      } else {
        console.error('Unknown item type:', type);
        return;
      }
      
      // Open in new tab
      window.open(itemUrl, '_blank', 'noopener,noreferrer');
      
    } catch (error) {
      console.error(`Failed to open ${type.slice(0, -1)}:`, error);
      // Fallback: try to open without instance
      let fallbackUrl;
      if (type === 'leads') {
        fallbackUrl = `https://dev.assistgo.de/lead/${itemId}/details`;
      } else if (type === 'campaigns') {
        fallbackUrl = `https://dev.assistgo.de/campaign/${itemId}/details`;
      }
      
      if (fallbackUrl) {
        window.open(fallbackUrl, '_blank', 'noopener,noreferrer');
      }
    }
  }

  async createNotifySection() {
    const section = document.createElement('div');
    section.className = 'wizard-section notify-section';

    const header = document.createElement('div');
    header.className = 'section-header';
    header.innerHTML = `
      <h3>${t('notify_title')}</h3>
      <p>${t('notify_desc')}</p>
    `;
    section.appendChild(header);

    try {
      // Load users if not already loaded
      if (this.wizard.usersData.size === 0) {
        await this.loadUsers();
      }

      // Create the multi-select user input
      const notifyContainer = this.createMultiSelectUserInput();
      section.appendChild(notifyContainer);

    } catch (error) {
      console.error('Failed to load notify users:', error);
      const errorDiv = document.createElement('div');
      errorDiv.className = 'notify-error';
      errorDiv.textContent = `${t('failed_load_users')} ${error.message}`;
      section.appendChild(errorDiv);
    }

    return section;
  }

  createMultiSelectUserInput() {
    const container = document.createElement('div');
    container.className = 'notify-input-container';

    const label = document.createElement('label');
    label.className = 'notify-label';
    label.textContent = t('notify_label');
    container.appendChild(label);

    // Selected users chips container
    const selectedUsersContainer = document.createElement('div');
    selectedUsersContainer.className = 'selected-users-chips';
    this.renderSelectedUserChips(selectedUsersContainer);
    container.appendChild(selectedUsersContainer);

    // Create searchable dropdown for users
    const allUsers = Array.from(this.wizard.usersData.values());
    
    const dropdown = new SearchableDropdown({
      placeholder: t('notify_placeholder'),
      items: allUsers,
      onSelect: (user) => this.selectUser(user._id, selectedUsersContainer),
      renderItem: (user, isSelected) => {
        const userInfo = document.createElement('div');
        userInfo.className = 'user-info notify-user-item';
        
        const userName = document.createElement('span');
        userName.className = 'user-name';
        userName.textContent = user.value || t('unknown_user');
        
        const userEmail = document.createElement('span');
        userEmail.className = 'user-email';
        userEmail.textContent = user.email || '';
        
        userInfo.appendChild(userName);
        if (user.email) {
          userInfo.appendChild(userEmail);
        }
        
        return userInfo;
      },
      isSelected: (user) => this.wizard.selectedNotifyUsers.includes(user._id),
      multiSelect: true,
      closeOnSelect: false,
      showIcon: false,
      showToggleIcon: true, // Show dropdown toggle icon (▼/▲)
      debounceMs: 0, // No debounce for static list
      additionalClasses: {
        wrapper: 'notify-input-wrapper',
        input: 'notify-input',
        dropdown: 'dropdown-menu notify-dropdown'
      }
    });

    container.appendChild(dropdown.render());
    this.notifyUsersDropdown = dropdown;

    return container;
  }

  selectUser(userId, selectedUsersContainer) {
    // Add user to selected list
    if (!this.wizard.selectedNotifyUsers.includes(userId)) {
      this.wizard.selectedNotifyUsers.push(userId);
    }

    // DEBUG: Log user selection
    const user = this.wizard.usersData.get(userId);
   
    // Re-render chips (dropdown will auto-update via isSelected callback)
    this.renderSelectedUserChips(selectedUsersContainer);
    
    // Clear the dropdown input
    if (this.notifyUsersDropdown) {
      this.notifyUsersDropdown.clear();
    }
  }

  renderSelectedUserChips(container) {
    container.innerHTML = '';
    
    if (this.wizard.selectedNotifyUsers.length === 0) {
      const placeholder = document.createElement('div');
      placeholder.className = 'no-users-selected';
      placeholder.textContent = t('no_users_selected');
      container.appendChild(placeholder);
      return;
    }

    this.wizard.selectedNotifyUsers.forEach(userId => {
      const user = this.wizard.usersData.get(userId);
      if (!user) return;

      const chip = document.createElement('div');
      chip.className = 'user-chip';
      
      const userNameSpan = document.createElement('span');
      userNameSpan.className = 'chip-name';
      userNameSpan.textContent = user.value || t('unknown_user');
      
      const removeBtn = document.createElement('button');
      removeBtn.className = 'chip-remove';
      removeBtn.innerHTML = '×';
      removeBtn.type = 'button';
      removeBtn.onclick = (e) => {
        e.stopPropagation();
        this.removeUser(userId, container);
      };
      
      chip.appendChild(userNameSpan);
      chip.appendChild(removeBtn);
      container.appendChild(chip);
    });
  }

  removeUser(userId, container) {
    // Remove user from selected list
    this.wizard.selectedNotifyUsers = this.wizard.selectedNotifyUsers.filter(id => id !== userId);
    
    // Re-render chips
    this.renderSelectedUserChips(container);
  }

  async loadUsers() {
    try {
      // Import the API function
      const { fetchAllUsers } = await import('../../services/api.js');
      
      const users = await fetchAllUsers();
      
      // Clear and populate users data
      this.wizard.usersData.clear();
      users.forEach(user => {
        this.wizard.usersData.set(user._id, user);
      });
      
    } catch (error) {
      console.error('Failed to load users:', error);
      throw error;
    }
  }

  async createAttachmentsSection() {
    const section = document.createElement('div');
    section.className = 'wizard-section attachments-section';

    const header = document.createElement('div');
    header.className = 'section-header';
    header.innerHTML = `
      <h3>${t('attachments_title')}</h3>
      <p>${t('attachments_desc')}</p>
    `;
    section.appendChild(header);

    try {
      // Import attachment functions
      const { extractEmailAttachments, detectExchangeCapabilities } = await import('../../services/api.js');
      
      // Extract attachments
      const allAttachments = await extractEmailAttachments(this.wizard.item);
      const attachments = allAttachments.filter(att => !att.isInline);
      
      // Create a virtual .msg file representing the email itself
      const emailMsgFile = {
        id: 'email-msg-file',
        name: `${this.wizard.item.subject || t('default_email_subject')}.msg`,
        size: this.wizard.item.body?.length || 0,
        contentType: 'application/vnd.ms-outlook',
        attachmentType: 'email-msg',
        isEmailItself: true
      };
      
      // Auto-select ALL attachments by default (including email .msg file)
      const allAttachmentIds = ['email-msg-file', ...attachments.map(att => att.id)];
      this.wizard.selectedAttachments = allAttachmentIds;
      
      // Add the email .msg file to the beginning of attachments list
      const allAttachmentsWithMsg = [emailMsgFile, ...attachments];
      
      // Check Exchange capabilities
      const capabilities = detectExchangeCapabilities();
      
      if (!capabilities.supportsAttachmentContent) {
        // Show warning for unsupported Exchange
        const warningDiv = document.createElement('div');
        warningDiv.className = 'attachment-warning';
        warningDiv.innerHTML = `
          <div style="display: flex; align-items: center; margin-bottom: 8px;">
            <span style="font-size: 16px; margin-right: 8px;">⚠️</span>
            <strong style="color: #856404;">${t('attachment_not_supported_title')}</strong>
          </div>
          <div style="font-size: 12px; color: #856404; margin-bottom: 8px;">
            ${t('attachment_not_supported_body')}
          </div>
          <div style="font-size: 11px; color: #6c757d;">
            <strong>${t('found_attachments')}</strong> ${allAttachmentsWithMsg.map(att => att.name).join(', ')}
          </div>
        `;
        section.appendChild(warningDiv);
      } else {
        // Header row with controls
        const headerRow = document.createElement('div');
        headerRow.className = 'attachments-header-row';

        const attachmentControls = document.createElement('div');
        attachmentControls.className = 'attachment-controls';
        attachmentControls.innerHTML = `
          <span class="attachments-quick-actions">
            <button type="button" class="control-chip" data-action="all" title="${t('select_all')}">${t('select_all')}</button>
            <button type="button" class="control-chip" data-action="none" title="${t('select_none')}">${t('select_none')}</button>
          </span>
        `;

        headerRow.appendChild(attachmentControls);
        section.appendChild(headerRow);

        const attachmentsList = document.createElement('div');
        attachmentsList.className = 'attachments-list-consolidated';

        allAttachmentsWithMsg.forEach(attachment => {
          const attachmentItem = this.createAttachmentItem(attachment);
          attachmentsList.appendChild(attachmentItem);
        });

        section.appendChild(attachmentsList);

        // Bind controls
        const allButton = attachmentControls.querySelector('[data-action="all"]');
        const noneButton = attachmentControls.querySelector('[data-action="none"]');
        
        allButton.onclick = () => {
          const checkboxes = attachmentsList.querySelectorAll('.attachment-checkbox');
          checkboxes.forEach(cb => {
            if (!cb.disabled) {
              cb.checked = true;
              if (!this.wizard.selectedAttachments.includes(cb.value)) {
                this.wizard.selectedAttachments.push(cb.value);
              }
            }
          });
        };

        noneButton.onclick = () => {
          const checkboxes = attachmentsList.querySelectorAll('.attachment-checkbox');
          checkboxes.forEach(cb => {
            if (!cb.disabled) {
              cb.checked = false;
              this.wizard.selectedAttachments = this.wizard.selectedAttachments.filter(id => id !== cb.value);
            }
          });
          // Ensure email .msg file stays selected even after deselect all
          if (!this.wizard.selectedAttachments.includes('email-msg-file')) {
            this.wizard.selectedAttachments.push('email-msg-file');
          }
        };
      }
      
    } catch (error) {
      console.error('Failed to load attachments:', error);
      const errorState = EmptyState.error(`${t('failed_load_attachments')} ${error.message}`);
      section.appendChild(errorState.render());
    }

    return section;
  }

  createAttachmentItem(attachment) {
    const item = document.createElement('div');
    item.className = 'attachment-item-consolidated';
    
    const fileSize = FileUtils.formatFileSize(attachment.size);
    const fileIcon = FileUtils.getFileIcon(attachment.name);
    
    // Check if this is the email .msg file (the email itself)
    const isEmailMsgFile = attachment.isEmailItself === true || attachment.id === 'email-msg-file';
    
    // Check if attachment is selected (treat .msg file like any other attachment)
    let isSelected = this.wizard.selectedAttachments.includes(attachment.id);
    
    item.innerHTML = `
      <div class="attachment-content">
        <div class="attachment-checkbox-wrapper">
          <input type="checkbox" class="attachment-checkbox" value="${attachment.id}" 
                 ${isSelected ? 'checked' : ''}
                 ${isEmailMsgFile ? 'disabled' : ''}>
        </div>
        <div class="attachment-icon">${fileIcon}</div>
        <div class="attachment-info">
          <div class="attachment-name" title="${attachment.name}">${attachment.name}</div>
          <div class="attachment-size">${fileSize}${isEmailMsgFile ? ` ${t('email_msg_always_included')}` : ''}</div>
        </div>
      </div>
    `;
    
    // Add checkbox handler for all attachments (except .msg file which is disabled)
    const checkbox = item.querySelector('.attachment-checkbox');
    if (!isEmailMsgFile) {
      checkbox.addEventListener('change', (e) => {
        if (e.target.checked) {
          if (!this.wizard.selectedAttachments.includes(attachment.id)) {
            this.wizard.selectedAttachments.push(attachment.id);
          }
        } else {
          this.wizard.selectedAttachments = this.wizard.selectedAttachments.filter(id => id !== attachment.id);
        }
      });
    }
    
    // Add special styling for email .msg files (optional visual distinction)
    if (isEmailMsgFile) {
      item.classList.add('attachment-email-msg');
    }
    
    return item;
  }

  // Reload leads and campaigns when contacts change
  async reloadLeadsAndCampaigns() {
    await this.loadLeadsAndCampaigns();
    
    // Update the dropdown inputs with new data
    const leadsContainer = document.querySelector('.leads-container');
    const campaignsContainer = document.querySelector('.campaigns-container');
    
    if (leadsContainer) {
      const newLeadsContainer = this.createDropdownInput('leads', t('select_lead'), this.wizard.leadsData);
      leadsContainer.replaceWith(newLeadsContainer);
    }
    
    if (campaignsContainer) {
      const newCampaignsContainer = this.createDropdownInput('campaigns', t('select_campaign'), this.wizard.campaignsData);
      campaignsContainer.replaceWith(newCampaignsContainer);
    }
  }

  // Load leads and campaigns data
  async loadLeadsAndCampaigns() {
    try {
      // Clear existing data
      this.wizard.leadsData.clear();
      this.wizard.campaignsData.clear();
      
      // Import the API function
      const { fetchLeadsByContacts } = await import('../../services/api.js');
      
      console.log('Loading leads for contacts:', this.wizard.selectedContacts.map(c => c._id));
      
      try {
        // Use the new bulk leads fetching function
        const result = await fetchLeadsByContacts(this.wizard.selectedContacts);
        console.log('Bulk leads result:', result);
        const leads = result?.leads || [];
        
        leads.forEach(lead => {
          // Use lead ID as key to avoid duplicates
          if (!this.wizard.leadsData.has(lead._id)) {
            this.wizard.leadsData.set(lead._id, {
              ...lead,
              contactName: t('multiple_contacts'),
              contactId: 'multiple'
            });
          }
        });
      } catch (error) {
        console.error('Failed to load leads for contacts:', error);
      }
      
      console.log('Total leads loaded:', this.wizard.leadsData.size);
      
      // Load campaigns from selected contacts
      console.log('Loading campaigns from contacts...');
      for (const contact of this.wizard.selectedContacts) {
        if (contact.campaigns && Array.isArray(contact.campaigns)) {
          console.log(`Contact ${contact.value} has ${contact.campaigns.length} campaigns`);
          
          contact.campaigns.forEach(campaign => {
            // Use campaign ID as key to avoid duplicates
            if (!this.wizard.campaignsData.has(campaign._id)) {
              this.wizard.campaignsData.set(campaign._id, {
                ...campaign,
                contactName: contact.value || t('unknown_contact'),
                contactId: contact._id
              });
            }
          });
        } else {
          console.log(`Contact ${contact.value} has no campaigns or campaigns is not an array`);
        }
      }
      
      console.log('Total campaigns loaded:', this.wizard.campaignsData.size);
      
    } catch (error) {
      console.error('Failed to load leads and campaigns:', error);
    }
  }
}
